// ** React Imports
import { useMemo } from "react";

// ** Layout & components (Flix)
import FlixLayout from "src/flix/layouts/FlixLayout";
import FlixHero from "src/flix/components/FlixHero";
import FlixRow from "src/flix/components/FlixRow";
import { FlixEmpty, FlixError } from "src/flix/components/FlixStates";

// ** Data / utils (reused)
import { useFlixCatalog } from "src/flix/hooks/useFlixCatalog";
import { useAuth } from "src/hooks/useAuth";
import { courseProgress } from "src/@core/utils/course-progress";
import { pickContinueCourse } from "src/@core/utils/course-continue";

/******************************************************************
 * ##: FlixCatalog
 * Netflix-style catalog page (Flix equivalent of /home). Presentation
 * only: data comes from useFlixCatalog (reusing courses_list) and rows
 * are derived client-side by progress state, since courses carry no
 * category metadata. The hero rotates through the started courses.
 * @returns {JSX.Element} - the Flix catalog
 * History:
 * 22-07-2026: Created
 ******************************************************************/
const FlixCatalog = () => {
  const auth = useAuth();
  const { loading, error, courses } = useFlixCatalog(auth?.user);

  const rows = useMemo(() => buildRows(courses), [courses]);

  return (
    <FlixLayout>
      <FlixHero courses={rows.featured} loading={loading} />

      {error ? (
        <FlixError />
      ) : loading ? (
        <FlixRow title="Os meus cursos" loading />
      ) : courses.length === 0 ? (
        <FlixEmpty />
      ) : (
        <>
          <FlixRow title="Continuar a ver" courses={rows.continueWatching} variant="continue" />
          {rows.byProducer.map((group) => (
            <FlixRow key={group.id} title={group.name} courses={group.courses} />
          ))}
          <FlixRow title="Concluídos" courses={rows.completed} />
        </>
      )}
    </FlixLayout>
  );
};

/******************************************************************
 * ##: buildRows
 * Derives the catalog rows and the featured set from the flat course
 * list (no category metadata exists). Rows are progress-based:
 * continue-watching (in progress: >0% and <100%) and completed. The hero
 * rotates through the in-progress courses, ordered from the most recently
 * accessed to the oldest (see orderByLastAccess); when nothing is in
 * progress it falls back to a single course so the hero is never empty
 * (a one-item list simply does not rotate).
 * @param {Array} courses - course records from /users/courses
 * @returns {Object} - { featured, continueWatching, completed, byProducer }
 * History:
 * 22-07-2026: Created
 * 07-10-2026: "Continuar a ver" gates on real progress (>0%); "Os meus cursos" split into one row per producer
 ******************************************************************/
const buildRows = (courses) => {
  if (!Array.isArray(courses) || courses.length === 0) {
    return { featured: [], continueWatching: [], completed: [], byProducer: [] };
  }

  // In-progress only: strictly >0% and <100%. Don't trust the course-level
  // `started` flag — buildFlixModuleCards spreads it onto every module card, so
  // it can't tell an actually-begun module from a 0% one. Real progress can.
  const inProgress = courses.filter((course) => {
    const progress = courseProgress(course);
    return progress > 0 && progress < 100;
  });

  const completed = courses.filter((course) => courseProgress(course) >= 100);

  // Hero rotation set: in-progress, most recently accessed first.
  const continueWatching = orderByLastAccess(inProgress);

  // Never leave the hero empty (e.g. nothing started yet): fall back to a
  // single sensible course to feature.
  const featured = continueWatching.length
    ? continueWatching
    : [pickContinueCourse(courses).target || courses[0]].filter(Boolean);

  // "Os meus cursos" split into one row per producer (first-seen order, which is
  // most-recent-first since courses arrive sorted by added_at desc).
  const byProducer = groupByProducer(courses);

  return { featured, continueWatching, completed, byProducer };
};

/******************************************************************
 * ##: groupByProducer
 * Groups courses/module cards into one bucket per producer, titled by
 * `producer_name` (stamped by the backend getFlixCourses). Insertion order
 * is preserved (Map), so the producer of the most recently added course
 * comes first. Cards without a producer name fall back to "Outros cursos".
 * @param {Array} courses - catalog cards (courses and/or module cards)
 * @returns {Array} - [{ id, name, courses: Array }] in first-seen order
 * History:
 * 07-10-2026: Created
 ******************************************************************/
const groupByProducer = (courses) => {
  const groups = new Map();
  for (const course of courses) {
    const id = course?.producer_id ?? "__unknown__";
    if (!groups.has(id)) groups.set(id, { id, name: course?.producer_name || "Outros cursos", courses: [] });
    groups.get(id).courses.push(course);
  }
  return [...groups.values()];
};

/******************************************************************
 * ##: orderByLastAccess
 * Sorts courses from the most recently accessed to the oldest. The
 * recency key is `courses_students.last_access` (a backend timestamp);
 * the course opened last in this browser (flix:last_course) is always
 * pinned first so the ordering feels right immediately, before a
 * refetch reflects the new access time. Courses with no timestamp keep
 * their original relative order (stable sort).
 * @param {Array} courses - course records to order
 * @returns {Array} - a new, recency-ordered array
 * History:
 * 22-07-2026: Created
 ******************************************************************/
const orderByLastAccess = (courses) => {
  const lastOpened = readLastOpened();
  return courses
    .map((course, i) => ({ course, i, key: recencyOf(course, lastOpened) }))
    .sort((a, b) => b.key - a.key || a.i - b.i)
    .map((entry) => entry.course);
};

/******************************************************************
 * ##: recencyOf
 * Recency key (ms) for a course. Prefers the last opened course in this
 * browser, then the backend `last_access` timestamp. Returns 0 when
 * neither is available (course sorts last, original order preserved).
 * @param {Object} course - one course record
 * @param {Object|null} lastOpened - { id, at } from localStorage
 * @returns {Number} - recency in ms (higher = more recent)
 * History:
 * 22-07-2026: Created
 ******************************************************************/
const recencyOf = (course, lastOpened) => {
  if (lastOpened && String(course?.course_id) === lastOpened.id) {
    return lastOpened.at || Number.MAX_SAFE_INTEGER;
  }
  return toMs(course?.courses_students?.last_access);
};

/******************************************************************
 * ##: toMs
 * Parses a date value (ISO string or epoch number) to a millisecond
 * timestamp. Returns 0 for missing or unparseable values.
 * @param {String|Number} value - the date value
 * @returns {Number} - ms since epoch, or 0
 * History:
 * 22-07-2026: Created
 ******************************************************************/
const toMs = (value) => {
  if (value == null) return 0;
  const ms = typeof value === "number" ? value : new Date(value).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

/******************************************************************
 * ##: readLastOpened
 * Reads the last opened Flix course from localStorage (written by the
 * player on open) as { id, at }. Returns null when absent or unreadable.
 * @returns {Object|null} - { id: String, at: Number } or null
 * History:
 * 22-07-2026: Created
 ******************************************************************/
const readLastOpened = () => {
  try {
    const raw = window.localStorage.getItem("flix:last_course");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.id == null) return null;
    return { id: String(parsed.id), at: Number(parsed.at) || 0 };
  } catch (e) {
    return null;
  }
};

export default FlixCatalog;
